// src/pages/ResetPassword.jsx
import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';

export default function ResetPassword() {
  const [searchParams]              = useSearchParams();
  const token                       = searchParams.get('token') || '';
  const navigate                    = useNavigate();

  const [form, setForm]             = useState({ new_password: '', confirm: '' });
  const [loading, setLoading]       = useState(false);
  const [success, setSuccess]       = useState(false);
  const [error, setError]           = useState(null);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (form.new_password !== form.confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (form.new_password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (!token) {
      setError('Missing reset token. Please use the link from your email.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ token, new_password: form.new_password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>TraderAIgo</h1>
          <div style={styles.errorBox}>
            Invalid reset link. Please request a new one.
          </div>
          <p style={styles.footer}>
            <Link to="/forgot-password" style={styles.link}>Request new reset link</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoWrap}>
          <img src="/logo.png" alt="TraderAIgo" style={styles.logo} />
        </div>
        <h1 style={styles.title}>TraderAIgo</h1>
        <h2 style={styles.subtitle}>Set New Password</h2>

        {success ? (
          <div style={styles.success}>
            ✅ Password updated! Redirecting to sign in…
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            <label style={styles.label}>New password</label>
            <input
              name="new_password"
              type="password"
              autoComplete="new-password"
              value={form.new_password}
              onChange={handleChange}
              required
              minLength={6}
              style={styles.input}
              placeholder="At least 6 characters"
            />

            <label style={styles.label}>Confirm password</label>
            <input
              name="confirm"
              type="password"
              autoComplete="new-password"
              value={form.confirm}
              onChange={handleChange}
              required
              style={styles.input}
            />

            {error && <div style={styles.errorBox}>{error}</div>}

            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        )}

        <p style={styles.footer}>
          <Link to="/login" style={styles.link}>Back to Sign In</Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f2f5',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: '2.5rem 2rem',
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
  },
  logoWrap: { textAlign: 'center', marginBottom: '0.5rem' },
  logo: { width: 52, height: 52, objectFit: 'contain', borderRadius: 10 },
  title: {
    margin: '0 0 0.25rem',
    fontSize: '1.6rem',
    color: '#1976d2',
    textAlign: 'center',
  },
  subtitle: {
    margin: '0 0 1.5rem',
    fontSize: '1.1rem',
    color: '#555',
    textAlign: 'center',
    fontWeight: 400,
  },
  form: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  label: { fontSize: '0.85rem', color: '#444', fontWeight: 600, marginTop: '0.5rem' },
  input: {
    padding: '0.65rem 0.75rem',
    fontSize: '1rem',
    border: '1px solid #ccc',
    borderRadius: 6,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  errorBox: {
    color: '#c62828',
    backgroundColor: '#ffebee',
    border: '1px solid #ef9a9a',
    borderRadius: 6,
    padding: '0.5rem 0.75rem',
    fontSize: '0.9rem',
    marginTop: '0.25rem',
  },
  success: {
    backgroundColor: '#e8f5e9',
    border: '1px solid #a5d6a7',
    borderRadius: 8,
    padding: '1rem',
    color: '#2e7d32',
    fontSize: '0.92rem',
    textAlign: 'center',
  },
  button: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  footer: {
    marginTop: '1.5rem',
    textAlign: 'center',
    fontSize: '0.9rem',
    color: '#666',
  },
  link: { color: '#1976d2', textDecoration: 'none', fontWeight: 600 },
};
